import { Typography } from '@mui/material';
import { atom, useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { noop } from 'lodash-es';
import { GenericFallbacksWrapper } from '../../../../../components/Fallback/GenericFallbacksWrapper';
import { Loader } from '../../../../../components/Loader/Loader';
import { LoadingStatus } from '../../../../../types';
import { useAsync } from '../../../../../util/hook-utils';
import {
  KPIReviewState,
  loadReviewerViewInitialData,
  ReviewerViewData,
  useKPIReviewerActions,
} from './hooks/useKPIRequestReviewActions';
import { ArchivedRequestNotice } from './KPIReviewerView';
import { KPIRenderedTemplate } from './KPIRenderedTemplate';
import {
  Background,
  headerWrapperCSS,
  headerWrapperInnerCSS,
  kpiContentsInnerWrapperCss,
  kpiContentsWrapperCss,
  statusWrapperCss,
} from './KpiViewCommonStyles';
import { ReviewResponseDialog } from './ReviewResponseDialog';
import { KpiResponseStatus } from './KpiResponseStatus';
import { ManagedServiceHeader } from './ManagedServiceHeader';
import { useManagedServiceActions } from './hooks/useManagedServiceActions';
import { ManagedServiceKpiGrid } from './ManagedServiceKpiGrid';
export function ManagedServiceView() {
  const { requestId } = useParams<{ requestId: string }>();
  const { error, loading, data } = useAsync(
    useCallback(() => loadReviewerViewInitialData(Number(requestId)), [requestId])
  );

  switch (loading) {
    case LoadingStatus.loading:
      return <Loader />;

    case LoadingStatus.error:
      return <Typography>{error}</Typography>;

    case LoadingStatus.success:
    default:
      if (data?.archived) {
        // Managed-service users have no access to the Deleted Requests tab.
        return <ArchivedRequestNotice company={data.company} showDeletedRequestsLink={false} />;
      }
      return <Layout initialData={data} />;
  }
}

type LayoutProps = {
  initialData: ReviewerViewData;
};

function Layout(props: LayoutProps) {
  const componentState = useMemo(
    () =>
      atom<KPIReviewState>({
        ...props.initialData,
        isModified: false,
        isSubmitting: false,
        submissionError: null,
      }),
    [props.initialData]
  );
  const { currentRequest, currentResponse, currentTemplate, isSubmitting } = useAtomValue(componentState);

  const { startReview, finishReview } = useManagedServiceActions(componentState);
  const { rejectResponse, moveBackToReview } = useKPIReviewerActions(componentState);

  const [showRejectDialog, setShowRejectDialog] = useState(false);

  const handleStartReview = useCallback(async () => {
    await startReview();
  }, [startReview]);

  const handleReject = useCallback(
    async (comment?: string) => {
      await rejectResponse(comment);
      setShowRejectDialog(false);
    },
    [rejectResponse]
  );

  return (
    <Background>
      {showRejectDialog && (
        <ReviewResponseDialog
          company={props.initialData.company}
          request={currentRequest}
          onSubmit={handleReject}
          onCancel={() => setShowRejectDialog(false)}
          action='reject'
        />
      )}
      <div css={headerWrapperCSS}>
        <div css={headerWrapperInnerCSS}>
          <ManagedServiceHeader
            company={props.initialData.company}
            isSubmitting={isSubmitting}
            kpiRequest={currentRequest}
            onFinishReview={finishReview}
            onRejectResponse={() => setShowRejectDialog(true)}
            onStartReview={handleStartReview}
            onMoveBackToReview={moveBackToReview}
            submittedBy={currentResponse?.submittedBy}
            respondedAt={currentResponse?.respondedAt}
          />
          <div css={statusWrapperCss}>
            <KpiResponseStatus
              status={currentRequest.status}
              acceptMessage={currentRequest.acceptMessage}
              rejectReason={currentRequest.rejectReason}
            />
          </div>
        </div>
      </div>
      <div css={kpiContentsWrapperCss}>
        <div css={kpiContentsInnerWrapperCss}>
          <GenericFallbacksWrapper>
            {currentTemplate ? (
              <KPIRenderedTemplate
                onKPIGridDataUpdated={noop}
                onSectionDataUpdated={noop}
                onTextualKpiUpdated={noop}
                request={currentRequest}
                response={currentResponse}
                template={currentTemplate}
                disabled
              />
            ) : (
              <Typography>Invalid Template</Typography>
            )}
          </GenericFallbacksWrapper>
        </div>
        <ManagedServiceKpiGrid request={currentRequest} />
      </div>
    </Background>
  );
}
